import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { LocalAgentsService } from 'src/app/services/local-agents/local-agents.service';
import { LocalAgentApproval, LocalAgentDecision } from 'src/app/services/local-agents/local-agents.model';

/**
 * The approvals surface for LOCAL agents (10-shell-owned-agents.md).
 *
 * Every row here is a **blocked tool call**: an agent running in the desktop shell
 * asked for something outside its workspace (or to run a command), and the shell
 * parked the call to wait for a human. Nothing times out and silence is never consent
 * — so the longer this sits unanswered, the longer that agent sits frozen mid-run,
 * which is why it lives in the app shell's banner slot rather than a page someone
 * would have to visit. The desktop tray shows the same truth ("N approvals waiting
 * for you") for the window-closed case.
 *
 * The buttons come from `allowedDecisions`, which is authoritative shell-side: a
 * critical request (write outside the workspace, run a command) never offers
 * `allow-always`, and the shell refuses any decision a request did not offer — so
 * there is nothing to enforce here, only to render.
 *
 * Cloud agents' approvals are a different mechanism on purpose (approval cards in the
 * conversation, resolved via the `decideAgentApproval` callable against the pod);
 * these never reach the cloud at all — the decision goes straight back over the
 * bridge, because the thing that is waiting is a promise inside this machine's shell.
 *
 * Renders nothing on the web and under Capacitor: with no shell there is no source of
 * approvals, and the service's signal stays empty.
 */
@Component({
  selector: 'app-local-agent-approvals',
  templateUrl: './local-agent-approvals.component.html',
  styleUrls: ['./local-agent-approvals.component.scss'],
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class LocalAgentApprovalsComponent {
  private readonly localAgents = inject(LocalAgentsService);

  /** Ids with a decision in flight — their buttons disable until the shell answers. */
  private readonly resolving = signal<ReadonlySet<string>>(new Set());

  readonly approvals = this.localAgents.approvals;

  isResolving(approval: LocalAgentApproval): boolean {
    return this.resolving().has(approval.id);
  }

  labelKey(decision: LocalAgentDecision): string {
    return `local-agents.decision.${decision}`;
  }

  async decide(approval: LocalAgentApproval, decision: LocalAgentDecision): Promise<void> {
    if (this.isResolving(approval)) return;
    this.resolving.update((ids) => new Set(ids).add(approval.id));
    try {
      await this.localAgents.resolveApproval(approval.id, decision);
      // The row disappears via the `agents.approvals` push; nothing to remove here.
    } finally {
      this.resolving.update((ids) => {
        const next = new Set(ids);
        next.delete(approval.id);
        return next;
      });
    }
  }
}
